import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Camera, Leaf, Lock, Package, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Reveal from "@/components/Reveal";

const STAGES = ["Order Placed", "Confirmed & Packed", "Shipped", "Out for Delivery", "Delivered"];
const TYPES = ["Serum", "Face Oil", "Moisturizer", "Cleanser", "Mask", "Toner", "Eye Care", "Hair Oil", "Exfoliant", "Gift Set"];
const BADGES = ["", "Bestseller", "New", "Limited Edition"];

const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
const csv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
const parsePairs = (s) => lines(s).map((l) => { const [a, b] = l.split("|").map((x) => (x || "").trim()); return { name: a, amount: b || "" }; });
const parseVariants = (s) => lines(s).map((l) => { const [a, b] = l.split("|").map((x) => (x || "").trim()); return { label: a, price: parseInt(b) || 0 }; });

const EMPTY = {
  name: "", tagline: "", type: "Serum", badge: "", price: "", mrp: "",
  concerns: "glow", skin_types: "normal, dry", description: "",
  benefits: "", how_to_use: "", actives: "Hero Ingredient | 2%", variants: "30ml | 999",
};

const inputCls = "w-full rounded-xl border bg-transparent px-4 py-2.5 text-sm outline-none transition-colors focus:border-[var(--mn-gold)] placeholder:text-[var(--mn-text2)]";

function ProductForm({ product, onClose, onSaved }) {
  const isEdit = !!product?.slug;
  const [f, setF] = useState(() =>
    product
      ? {
          name: product.name, tagline: product.tagline || "", type: product.type || "Serum",
          badge: product.badge || "", price: String(product.price), mrp: String(product.mrp || ""),
          concerns: (product.concerns || []).join(", "), skin_types: (product.skin_types || []).join(", "),
          description: product.description || "", benefits: (product.benefits || []).join("\n"),
          how_to_use: (product.how_to_use || []).join("\n"),
          actives: (product.actives || []).map((a) => `${a.name} | ${a.amount}`).join("\n"),
          variants: (product.variants || []).map((v) => `${v.label} | ${v.price}`).join("\n"),
        }
      : EMPTY
  );
  const [images, setImages] = useState(product?.images || []);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(-2); // -2 idle, -1 add, else index
  const fileRef = useRef(null);
  const imgIndexRef = useRef(-1);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const pickImage = (idx) => {
    imgIndexRef.current = idx;
    fileRef.current?.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB."); return; }
    setUploading(imgIndexRef.current);
    try {
      const res = await api.adminUploadProductImage(product.slug, file, imgIndexRef.current);
      setImages(res.images);
      toast.success("Photo updated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(-2);
    }
  };

  const save = async () => {
    if (!f.name.trim() || !parseInt(f.price)) { toast.error("Name and price are required."); return; }
    setSaving(true);
    const payload = {
      name: f.name.trim(), tagline: f.tagline, type: f.type, badge: f.badge || null,
      price: parseInt(f.price), mrp: parseInt(f.mrp) || parseInt(f.price),
      concerns: csv(f.concerns), skin_types: csv(f.skin_types), description: f.description,
      benefits: lines(f.benefits), how_to_use: lines(f.how_to_use),
      actives: parsePairs(f.actives), variants: parseVariants(f.variants),
    };
    try {
      if (isEdit) await api.adminUpdateProduct(product.slug, payload);
      else await api.adminCreateProduct(payload);
      toast.success(isEdit ? "Product updated." : "Product created.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <motion.div className="fixed inset-0 z-[160] bg-black/70 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
      <motion.div
        data-testid="product-form-modal"
        className="fixed inset-x-4 top-[4vh] z-[170] mx-auto max-h-[92vh] max-w-2xl overflow-y-auto rounded-3xl glass p-6 sm:p-8"
        style={{ background: "var(--mn-bg2)" }}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
      >
        <div className="flex items-center justify-between">
          <p className="font-serif text-xl">{isEdit ? "Edit Formula" : "New Formula"}</p>
          <button data-testid="pf-close" onClick={onClose} aria-label="Close" className="btn-outline-glow flex h-9 w-9 items-center justify-center rounded-full"><X size={15} /></button>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="eyebrow mb-1.5 block">Name *</label>
            <input data-testid="pf-name" value={f.name} onChange={set("name")} className={inputCls} style={{ borderColor: "var(--mn-glass-border)" }} />
          </div>
          <div className="sm:col-span-2">
            <label className="eyebrow mb-1.5 block">Tagline</label>
            <input data-testid="pf-tagline" value={f.tagline} onChange={set("tagline")} className={inputCls} style={{ borderColor: "var(--mn-glass-border)" }} />
          </div>
          <div>
            <label className="eyebrow mb-1.5 block">Type</label>
            <select data-testid="pf-type" value={f.type} onChange={set("type")} className={inputCls} style={{ borderColor: "var(--mn-glass-border)", background: "var(--mn-surface)" }}>
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="eyebrow mb-1.5 block">Badge</label>
            <select data-testid="pf-badge" value={f.badge} onChange={set("badge")} className={inputCls} style={{ borderColor: "var(--mn-glass-border)", background: "var(--mn-surface)" }}>
              {BADGES.map((b) => <option key={b} value={b}>{b || "None"}</option>)}
            </select>
          </div>
          <div>
            <label className="eyebrow mb-1.5 block">Price (₹) *</label>
            <input data-testid="pf-price" type="number" value={f.price} onChange={set("price")} className={inputCls} style={{ borderColor: "var(--mn-glass-border)" }} />
          </div>
          <div>
            <label className="eyebrow mb-1.5 block">MRP (₹)</label>
            <input data-testid="pf-mrp" type="number" value={f.mrp} onChange={set("mrp")} className={inputCls} style={{ borderColor: "var(--mn-glass-border)" }} />
          </div>
          <div>
            <label className="eyebrow mb-1.5 block">Concerns (comma separated)</label>
            <input data-testid="pf-concerns" value={f.concerns} onChange={set("concerns")} placeholder="glow, acne, dryness" className={inputCls} style={{ borderColor: "var(--mn-glass-border)" }} />
          </div>
          <div>
            <label className="eyebrow mb-1.5 block">Skin types (comma separated)</label>
            <input data-testid="pf-skin" value={f.skin_types} onChange={set("skin_types")} placeholder="oily, dry, normal" className={inputCls} style={{ borderColor: "var(--mn-glass-border)" }} />
          </div>
          <div className="sm:col-span-2">
            <label className="eyebrow mb-1.5 block">Description</label>
            <textarea data-testid="pf-description" rows={3} value={f.description} onChange={set("description")} className={inputCls} style={{ borderColor: "var(--mn-glass-border)" }} />
          </div>
          <div>
            <label className="eyebrow mb-1.5 block">Benefits (one per line)</label>
            <textarea data-testid="pf-benefits" rows={3} value={f.benefits} onChange={set("benefits")} className={inputCls} style={{ borderColor: "var(--mn-glass-border)" }} />
          </div>
          <div>
            <label className="eyebrow mb-1.5 block">How to use (one per line)</label>
            <textarea data-testid="pf-ritual" rows={3} value={f.how_to_use} onChange={set("how_to_use")} className={inputCls} style={{ borderColor: "var(--mn-glass-border)" }} />
          </div>
          <div>
            <label className="eyebrow mb-1.5 block">Actives (Name | % per line)</label>
            <textarea data-testid="pf-actives" rows={3} value={f.actives} onChange={set("actives")} className={inputCls} style={{ borderColor: "var(--mn-glass-border)" }} />
          </div>
          <div>
            <label className="eyebrow mb-1.5 block">Variants (Label | ₹ per line)</label>
            <textarea data-testid="pf-variants" rows={3} value={f.variants} onChange={set("variants")} className={inputCls} style={{ borderColor: "var(--mn-glass-border)" }} />
          </div>
        </div>

        {isEdit && (
          <div className="mt-6">
            <label className="eyebrow mb-2 block">Photos</label>
            <div className="flex flex-wrap gap-3">
              {images.map((src, i) => (
                <div key={i} className="relative">
                  <img src={src} alt="" className="h-20 w-16 rounded-xl object-cover" style={{ border: "1px solid var(--mn-glass-border)" }} />
                  <button
                    data-testid={`pf-replace-image-${i}`}
                    onClick={() => pickImage(i)}
                    className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/55 opacity-0 transition-opacity hover:opacity-100"
                    aria-label={`Replace photo ${i + 1}`}
                  >
                    {uploading === i ? <Leaf size={15} className="animate-spin" style={{ color: "var(--mn-gold)" }} /> : <Camera size={15} className="text-white" />}
                  </button>
                </div>
              ))}
              <button
                data-testid="pf-add-image"
                onClick={() => pickImage(-1)}
                className="flex h-20 w-16 items-center justify-center rounded-xl border border-dashed transition-colors hover:border-[var(--mn-gold)]"
                style={{ borderColor: "var(--mn-glass-border)", color: "var(--mn-text2)" }}
                aria-label="Add photo"
              >
                {uploading === -1 ? <Leaf size={15} className="animate-spin" /> : <Plus size={15} />}
              </button>
            </div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} className="hidden" data-testid="pf-image-input" />
          </div>
        )}

        <div className="mt-7 flex gap-3">
          <button data-testid="pf-save" onClick={save} disabled={saving} className="btn-gold flex flex-1 items-center justify-center gap-2 rounded-full py-3.5 text-sm">
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Product"}
          </button>
          <button data-testid="pf-cancel" onClick={onClose} className="btn-outline-glow rounded-full px-6 text-sm">Cancel</button>
        </div>
      </motion.div>
    </>
  );
}

export default function Admin() {
  const { user, loading, login } = useAuth();
  const [allowed, setAllowed] = useState(null);
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(undefined);

  const load = () => {
    api.getProducts({}).then(setProducts).catch(() => {});
    api.adminGetOrders().then(setOrders).catch(() => {});
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { setAllowed(false); return; }
    api.adminCheck().then(() => { setAllowed(true); load(); }).catch(() => setAllowed(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  if (allowed === null || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="admin-loading">
        <Leaf size={28} className="animate-pulse" style={{ color: "var(--mn-gold)" }} />
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4" data-testid="admin-denied">
        <Reveal className="glass-card w-full max-w-md rounded-[2rem] p-10 text-center">
          <Lock size={30} className="mx-auto" style={{ color: "var(--mn-terra)" }} />
          <h1 className="mt-5 font-serif text-2xl">This room is for the owner.</h1>
          <p className="mt-2 text-sm" style={{ color: "var(--mn-text2)" }}>
            {user ? `Signed in as ${user.email} — that account isn't the owner.` : "Sign in with the owner's Google account to manage the store."}
          </p>
          {!user && (
            <button data-testid="admin-signin" onClick={login} className="btn-gold mt-6 rounded-full px-7 py-3.5 text-sm">Sign In with Google</button>
          )}
          <Link to="/" className="mt-4 inline-flex items-center gap-1.5 text-sm transition-colors hover:text-[var(--mn-gold)]" style={{ color: "var(--mn-text2)" }} data-testid="admin-home-link">
            <ArrowLeft size={13} /> Back to the store
          </Link>
        </Reveal>
      </div>
    );
  }

  const remove = async (slug) => {
    if (!window.confirm("Delete this product forever?")) return;
    try {
      await api.adminDeleteProduct(slug);
      toast.success("Product deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 pb-24 pt-28 sm:px-6" data-testid="admin-page">
      <Reveal>
        <p className="eyebrow">Owner's Room</p>
        <h1 className="mt-3 font-serif text-3xl tracking-tight sm:text-4xl">
          The store, <span className="gold-text italic">in your hands.</span>
        </h1>
        <p className="mt-2 text-sm" style={{ color: "var(--mn-text2)" }}>Signed in as {user.email}</p>
      </Reveal>

      <div className="mt-8 flex gap-1 rounded-full border p-1" style={{ borderColor: "var(--mn-glass-border)" }} data-testid="admin-tabs">
        {[["products", "Products"], ["orders", "Orders"]].map(([v, label]) => (
          <button
            key={v}
            data-testid={`admin-tab-${v}`}
            onClick={() => setTab(v)}
            className="flex-1 rounded-full px-5 py-2.5 text-sm transition-all"
            style={tab === v ? { background: "var(--mn-gold)", color: "#070D09", fontWeight: 600 } : { color: "var(--mn-text2)" }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div className="mt-8" data-testid="admin-products">
          <div className="mb-5 flex items-center justify-between">
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.25em]" style={{ color: "var(--mn-text2)" }}>{products.length} formulas</p>
            <button data-testid="admin-add-product" onClick={() => setEditing(null)} className="btn-gold flex items-center gap-2 rounded-full px-5 py-2.5 text-xs">
              <Plus size={14} /> New Product
            </button>
          </div>
          <div className="space-y-3">
            {products.map((p) => (
              <div key={p.slug} className="glass-card flex flex-wrap items-center gap-4 rounded-2xl p-4" data-testid={`admin-product-${p.slug}`}>
                <img src={p.images[0]} alt="" className="h-14 w-11 rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-serif text-base">{p.name}</p>
                  <p className="font-mono text-[0.62rem]" style={{ color: "var(--mn-text2)" }}>
                    {p.type} · {inr(p.price)}{p.badge ? ` · ${p.badge}` : ""}
                  </p>
                </div>
                <button data-testid={`admin-edit-${p.slug}`} onClick={() => setEditing(p)} className="btn-outline-glow flex items-center gap-1.5 rounded-full px-4 py-2 text-xs">
                  <Pencil size={12} /> Edit
                </button>
                <button data-testid={`admin-delete-${p.slug}`} onClick={() => remove(p.slug)} className="flex h-9 w-9 items-center justify-center rounded-full border transition-colors hover:border-[var(--mn-terra)] hover:text-[var(--mn-terra)]" style={{ borderColor: "var(--mn-glass-border)", color: "var(--mn-text2)" }} aria-label={`Delete ${p.name}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="mt-8 space-y-3" data-testid="admin-orders">
          {orders.length === 0 && <p className="text-sm" style={{ color: "var(--mn-text2)" }}>No orders yet.</p>}
          {orders.map((o) => (
            <div key={o.order_id} className="glass-card flex flex-wrap items-center gap-4 rounded-2xl p-4" data-testid={`admin-order-${o.order_id}`}>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: "var(--mn-surface)" }}>
                <Package size={17} style={{ color: "var(--mn-gold)" }} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-mono text-sm" style={{ color: "var(--mn-gold)" }}>{o.order_id} <span className="ml-1" style={{ color: "var(--mn-text)" }}>{inr(o.total)}</span></p>
                <p className="mt-0.5 font-mono text-[0.62rem]" style={{ color: "var(--mn-text2)" }}>
                  {o.customer.name} · {o.customer.phone} · {o.customer.city} · {o.payment_method.toUpperCase()} · {new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                </p>
              </div>
              <select
                data-testid={`stage-select-${o.order_id}`}
                value={o.status_stage ?? ""}
                onChange={async (e) => {
                  if (e.target.value === "") return;
                  try {
                    await api.adminSetOrderStatus(o.order_id, parseInt(e.target.value));
                    toast.success(`${o.order_id} → ${STAGES[parseInt(e.target.value)]}`);
                    load();
                  } catch {
                    toast.error("Status update failed.");
                  }
                }}
                className="glass rounded-full px-3 py-2 font-mono text-xs outline-none"
                style={{ background: "var(--mn-glass)", color: "var(--mn-text)" }}
              >
                <option value="" style={{ background: "#0D1812" }}>Auto (by age)</option>
                {STAGES.map((s, i) => <option key={s} value={i} style={{ background: "#0D1812" }}>{s}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {editing !== undefined && (
          <ProductForm product={editing} onClose={() => setEditing(undefined)} onSaved={load} />
        )}
      </AnimatePresence>
    </div>
  );
}
